import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFileText, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import Header from './Header';
import Footer from './Footer';

/* ── Étoiles pleine page (dark mode uniquement) ── */
const StarsCanvas = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
  display: block;
`;

const Star = styled.div`
  position: absolute;
  border-radius: 50%;
  background: white;
`;

function GlobalStars() {
  const stars = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 200; i++) {
      const size = Math.random() * 2 + 0.5;
      arr.push({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: size,
        height: size,
        opacity: Math.random() * 0.5 + 0.1,
        animDelay: Math.random() * 6,
        animDuration: Math.random() * 4 + 3,
      });
    }
    return arr;
  }, []);

  return (
    <StarsCanvas>
      {stars.map(s => (
        <Star
          key={s.id}
          style={{
            left: s.left,
            top: s.top,
            width: s.width,
            height: s.height,
            opacity: s.opacity,
            animation: `twinkle ${s.animDuration}s ease-in-out ${s.animDelay}s infinite alternate`,
          }}
        />
      ))}
    </StarsCanvas>
  );
}

const Main = styled.main`
  min-height: 100vh;
  width: 100%;
  padding-top: 0;
`;

// Navigation par sections
const SectionNav = styled(motion.div)`
  position: fixed;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 997;

  @media (max-width: 768px) {
    display: none;
  }

  @media (max-height: 500px) {
    display: none;
  }
`;

const NavArrow = styled(motion.button)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  color: var(--dark);
  border: 1px solid var(--gray-300);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
  
  body.dark-mode & {
    background: rgba(30, 40, 60, 0.85);
    color: var(--white);
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(6px);
  }
  
  &:hover {
    background: var(--primary);
    color: var(--white);
    border-color: var(--primary);
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    &:hover {
      transform: none;
      background: rgba(255, 255, 255, 0.95);
      color: var(--dark);
      border-color: var(--gray-300);
      
      body.dark-mode & {
        background: rgba(30, 40, 60, 0.85);
        color: var(--white);
        border-color: rgba(255, 255, 255, 0.2);
      }
    }
  }
  
  svg {
    font-size: 1.3rem;
  }
`;

const SectionDots = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0;
  
  @media (max-width: 480px) {
    display: none;
  }
`;

const SectionDot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? 'var(--secondary)' : 'var(--gray-400)'};
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
  
  body.dark-mode & {
    background: ${props => props.$active ? 'var(--secondary)' : 'var(--gray-600)'};
  }
  
  &:hover {
    background: var(--secondary);
    transform: scale(1.3);
  }
`;

const FloatingButton = styled(motion.button)`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--secondary) 0%, #e6b800 100%);
  color: var(--black);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(255, 206, 0, 0.4);
  z-index: 999;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    bottom: 1.5rem;
    right: 4rem;
    width: 50px;
    height: 50px;
  }
  
  @media (max-width: 480px) {
    bottom: 1rem;
    right: 3rem;
    width: 45px;
    height: 45px;
    
    svg {
      font-size: 1.2rem;
    }
  }
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(255, 206, 0, 0.5);
  }
  
  svg {
    font-size: 1.5rem;
  }
`;

const FloatingLabel = styled(motion.span)`
  position: fixed;
  bottom: 2.5rem;
  right: 5.5rem;
  background-color: var(--dark);
  color: var(--white);
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-sm);
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 998;
  
  &::after {
    content: '';
    position: absolute;
    right: -6px;
    top: 50%;
    transform: translateY(-50%);
    border-left: 6px solid var(--dark);
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
  
  .dark-mode & {
    background-color: var(--white);
    color: var(--dark);
    
    &::after {
      border-left-color: var(--white);
    }
  }
`;

const Layout = ({ children }) => {
  const [showFloating, setShowFloating] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  
  // Liste des sections dans l'ordre
  const sections = ['accueil', 'services', 'realisations', 'a-propos', 'contact', 'footer'];
  
  useEffect(() => {
    const handleScroll = () => {
      // Afficher le bouton flottant après avoir scrollé de 300px
      if (window.scrollY > 300) {
        setShowFloating(true);
      } else {
        setShowFloating(false);
      }
      
      // Déterminer la section actuelle
      // Si on est tout en haut, c'est la section accueil (Hero)
      if (window.scrollY < 50) {
        setCurrentSectionIndex(0);
        return;
      }
      
      // Si on est tout en bas, c'est le footer
      const isAtBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 50;
      if (isAtBottom) {
        setCurrentSectionIndex(sections.length - 1);
        return;
      }
      
      // Trouver la section visible au milieu de l'écran
      const viewportMiddle = window.scrollY + window.innerHeight / 2;
      
      let currentIndex = 0;
      for (let i = 0; i < sections.length; i++) {
        const section = document.getElementById(sections[i]);
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionBottom = sectionTop + section.offsetHeight;
          
          if (viewportMiddle >= sectionTop && viewportMiddle < sectionBottom) {
            currentIndex = i;
            break;
          } else if (viewportMiddle >= sectionTop) {
            currentIndex = i;
          }
        }
      }
      
      setCurrentSectionIndex(currentIndex);
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Appel initial
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);
  
  const scrollToDevis = () => {
    const devisSection = document.getElementById('devis');
    if (devisSection) {
      devisSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const goToPrevSection = () => {
    if (currentSectionIndex > 0) {
      const prevSection = sections[currentSectionIndex - 1];
      scrollToSection(prevSection);
    } else if (currentSectionIndex === 0) {
      // Si déjà sur accueil, s'assurer qu'on est bien en haut
      const section = document.getElementById('accueil');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };
  
  const goToNextSection = () => {
    if (currentSectionIndex < sections.length - 1) {
      scrollToSection(sections[currentSectionIndex + 1]);
    }
  };
  
  return (
    <>
      <GlobalStars />
      <Header />
      <Main>{children}</Main>
      <Footer />
      
      {/* Navigation par sections */}
      <SectionNav>
        <NavArrow 
          onClick={goToPrevSection}
          aria-label="Section précédente"
          whileTap={{ scale: 0.9 }}
        >
          <FiChevronUp />
        </NavArrow>
        
        <SectionDots>
          {sections.map((section, index) => (
            <SectionDot
              key={section}
              $active={index === currentSectionIndex}
              onClick={() => scrollToSection(section)}
              aria-label={`Aller à ${section}`}
            />
          ))}
        </SectionDots>
        
        <NavArrow 
          onClick={goToNextSection}
          aria-label="Section suivante"
          whileTap={{ scale: 0.9 }}
        >
          <FiChevronDown />
        </NavArrow>
      </SectionNav>

      <AnimatePresence>
        {showFloating && (
          <>
            <FloatingButton
              onClick={scrollToDevis}
              onMouseEnter={() => setShowLabel(true)}
              onMouseLeave={() => setShowLabel(false)}
              initial={{ opacity: 0, scale: 0, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              aria-label="Estimez votre projet"
            >
              <FiFileText />
            </FloatingButton>
            
            <AnimatePresence>
              {showLabel && (
                <FloatingLabel
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.2 }}
                >
                  Devis gratuit
                </FloatingLabel>
              )}
            </AnimatePresence>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Layout;
